import { useState, useEffect, useCallback } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import { useLanguage } from '../../contexts/LanguageContext'
import { wiseApi, type WiseConnection, type WiseBalanceRow } from '../../lib/api'
import { useAmountFormat } from '../../contexts/AmountUnitContext'
import { btnDangerSm, btnPrimary, btnPrimarySm, btnSecondarySm, subheading } from '../../lib/ui'

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString()
}

function BalanceRow({
  balance,
  onLink,
  onSync,
  busy,
}: {
  balance: WiseBalanceRow
  onLink: () => void
  onSync: (full: boolean) => void
  busy: boolean
}) {
  const { t } = useLanguage()
  const { formatCurrency } = useAmountFormat()
  const formattedAmount = formatCurrency(balance.amount, balance.currency)

  return (
    <li className="flex flex-wrap items-center justify-between gap-2 py-2.5 px-1">
      <div className="min-w-0">
        <p className="text-sm font-medium text-gray-800 dark:text-gray-200">
          {balance.name ?? `${balance.currency} (${balance.type})`}
        </p>
        <p className="text-xs text-gray-400 dark:text-gray-500">
          {formattedAmount}
          {balance.linked && (
            <>
              {' · '}
              {t('wise_last_synced')}: {balance.lastSyncedAt ? formatDateTime(balance.lastSyncedAt) : t('wise_never_synced')}
            </>
          )}
        </p>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {balance.linked ? (
          <>
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-lime-200 text-lime-900">
              {t('wise_linked_badge')}
            </span>
            <button
              onClick={() => { onSync(false) }}
              disabled={busy}
              className={btnPrimarySm}
            >
              {busy ? t('wise_syncing') : t('wise_sync_btn')}
            </button>
            {/* 前回同期以降しか見に行かないため、過去分をまとめて入れ直す口を用意する */}
            <button
              onClick={() => { onSync(true) }}
              disabled={busy}
              className={btnSecondarySm}
            >
              {t('wise_sync_full_btn')}
            </button>
          </>
        ) : (
          <button
            onClick={onLink}
            disabled={busy}
            className={btnSecondarySm}
          >
            {busy ? t('wise_linking') : t('wise_link_btn')}
          </button>
        )}
      </div>
    </li>
  )
}

export default function WiseIntegrationTab() {
  const { accessToken: token } = useAuth()
  const { t } = useLanguage()
  const [connections, setConnections] = useState<WiseConnection[]>([])
  const [balances, setBalances] = useState<Record<string, WiseBalanceRow[]>>({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [message, setMessage] = useState<string | null>(null)
  const [personalToken, setPersonalToken] = useState('')
  const [connecting, setConnecting] = useState(false)
  const [busyKey, setBusyKey] = useState<string | null>(null)

  const load = useCallback(async () => {
    if (!token) return
    try {
      const res = await wiseApi.listConnections(token)
      setConnections(res.data)
      const entries = await Promise.all(
        res.data.map(async (conn) => {
          try {
            const b = await wiseApi.listBalances(token, conn.id)
            return [conn.id, b.data] as const
          } catch {
            return [conn.id, []] as const
          }
        })
      )
      setBalances(Object.fromEntries(entries))
    } catch (err) {
      setError(err instanceof Error ? err.message : t('common_fetch_error'))
    } finally {
      setLoading(false)
    }
  }, [token, t])

  useEffect(() => { void load() }, [load])

  const handleConnect = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!token) return
    setConnecting(true)
    setError(null)
    setMessage(null)
    try {
      await wiseApi.connect(token, personalToken.trim())
      setPersonalToken('')
      await load()
    } catch (err) {
      setError(err instanceof Error ? err.message : t('common_save_error'))
    } finally {
      setConnecting(false)
    }
  }

  const handleDelete = async (conn: WiseConnection) => {
    if (!token) return
    if (!confirm(t('wise_delete_confirm').replace('{name}', conn.profileName))) return
    setError(null)
    setMessage(null)
    try {
      await wiseApi.deleteConnection(token, conn.id)
      await load()
    } catch (err) {
      setError(err instanceof Error ? err.message : t('common_delete_error'))
    }
  }

  const handleLink = async (connectionId: string, balance: WiseBalanceRow) => {
    if (!token) return
    setBusyKey(`${connectionId}:${balance.balanceId}`)
    setError(null)
    setMessage(null)
    try {
      await wiseApi.registerBalance(token, connectionId, { wiseBalanceId: balance.balanceId })
      await load()
    } catch (err) {
      setError(err instanceof Error ? err.message : t('common_save_error'))
    } finally {
      setBusyKey(null)
    }
  }

  const handleSync = async (connectionId: string, balance: WiseBalanceRow, full: boolean) => {
    if (!token || !balance.wiseAccountId) return
    setBusyKey(`${connectionId}:${balance.balanceId}`)
    setError(null)
    setMessage(null)
    try {
      const res = await wiseApi.sync(token, balance.wiseAccountId, full ? { full: true } : undefined)
      setMessage(
        t('wise_sync_result')
          .replace('{imported}', String(res.data.imported))
          .replace('{updated}', String(res.data.updated))
          .replace('{skipped}', String(res.data.skipped))
      )
      await load()
    } catch (err) {
      setError(err instanceof Error ? err.message : t('common_save_error'))
    } finally {
      setBusyKey(null)
    }
  }

  if (loading) return <p className="text-gray-500 text-sm dark:text-gray-400">{t('common_loading')}</p>

  return (
    <div className="space-y-8">
      <div>
        <h3 className={`${subheading} mb-2`}>{t('wise_title')}</h3>
        <p className="text-sm text-gray-500 mb-1 dark:text-gray-400">{t('wise_desc')}</p>
        <p className="text-xs text-gray-400 mb-4 dark:text-gray-500">{t('wise_token_note')}</p>
        {message && <p className="text-green-600 text-sm mb-3 dark:text-green-400">{message}</p>}
        {error && <p className="text-red-600 text-sm mb-3 dark:text-red-400">{error}</p>}
        <form onSubmit={(e) => { void handleConnect(e) }} className="flex gap-3 max-w-md">
          <input
            type="password"
            value={personalToken}
            onChange={(e) => setPersonalToken(e.target.value)}
            placeholder={t('wise_token_placeholder')}
            required
            autoComplete="off"
            className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
          />
          <button
            type="submit"
            disabled={connecting}
            className={btnPrimary}
          >
            {connecting ? t('wise_connecting') : t('wise_connect_btn')}
          </button>
        </form>
      </div>

      <div>
        <h3 className={`${subheading} mb-2`}>{t('wise_connections_title')}</h3>
        {connections.length === 0 ? (
          <p className="text-gray-400 text-sm dark:text-gray-500">{t('wise_no_connections')}</p>
        ) : (
          <div className="space-y-4">
            {connections.map((conn) => (
              <div key={conn.id} className="border border-gray-200 rounded-lg p-4 dark:border-gray-700">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <p className="text-sm font-medium text-gray-800 dark:text-gray-200">{conn.profileName}</p>
                    <p className="text-xs text-gray-400 dark:text-gray-500">
                      {conn.profileType} · {t('wise_last_synced')}: {conn.lastSyncedAt ? formatDateTime(conn.lastSyncedAt) : t('wise_never_synced')}
                    </p>
                  </div>
                  <button
                    onClick={() => { void handleDelete(conn) }}
                    className={btnDangerSm}
                  >
                    {t('wise_delete')}
                  </button>
                </div>
                <h4 className="text-xs font-semibold text-gray-500 mt-3 dark:text-gray-400">{t('wise_balances_title')}</h4>
                {(balances[conn.id] ?? []).length === 0 ? (
                  <p className="text-gray-400 text-sm py-2 dark:text-gray-500">{t('wise_balances_empty')}</p>
                ) : (
                  <ul className="divide-y divide-gray-100 dark:divide-gray-800">
                    {(balances[conn.id] ?? []).map((b) => (
                      <BalanceRow
                        key={b.balanceId}
                        balance={b}
                        busy={busyKey === `${conn.id}:${b.balanceId}`}
                        onLink={() => { void handleLink(conn.id, b) }}
                        onSync={(full) => { void handleSync(conn.id, b, full) }}
                      />
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
